import React, { useCallback, useEffect, useState } from "react";

import { toast } from "react-toastify";
import { Pencil, Trash2, Plus, Check, Minus } from "lucide-react";

import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import {
	Table,
	TableHeader,
	TableBody,
	TableRow,
	TableHead,
	TableCell,
} from "../../components/ui/table";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import Title from "../../components/Title";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import PlanModal from "../../components/PlanModal";
import ConfirmationModal from "../../components/ConfirmationModal";

const moeda = (valor) =>
	Number(valor || 0).toLocaleString("pt-BR", {
		style: "currency",
		currency: "BRL",
	});

const limite = (valor) => (Number(valor) > 0 ? valor : "Ilimitado");

const Plans = () => {
	const [plans, setPlans] = useState([]);
	const [loading, setLoading] = useState(false);
	const [modalOpen, setModalOpen] = useState(false);
	const [selecionado, setSelecionado] = useState(null);
	const [confirmOpen, setConfirmOpen] = useState(false);

	const carregar = useCallback(async () => {
		setLoading(true);
		try {
			const { data } = await api.get("/plans");
			setPlans(data);
		} catch (err) {
			toastError(err);
		}
		setLoading(false);
	}, []);

	useEffect(() => {
		carregar();
	}, [carregar]);

	const fecharModal = () => {
		setModalOpen(false);
		setSelecionado(null);
		carregar();
	};

	const excluir = async (id) => {
		try {
			await api.delete(`/plans/${id}`);
			toast.success("Plano excluído.");
			carregar();
		} catch (err) {
			toastError(err);
		}
		setSelecionado(null);
	};

	return (
		<MainContainer>
			<ConfirmationModal
				title={selecionado && `Apagar o plano ${selecionado.name}?`}
				open={confirmOpen}
				onClose={() => {
					setConfirmOpen(false);
					setSelecionado(null);
				}}
				onConfirm={() => excluir(selecionado.id)}
			>
				Se alguma empresa já estiver usando este plano, o sistema não deixa
				apagar. Nesse caso, o caminho é editar o plano e desmarcar “Plano
				disponível para venda”: ele some do catálogo e ninguém novo consegue
				contratá-lo.
			</ConfirmationModal>

			<PlanModal
				open={modalOpen}
				onClose={fecharModal}
				planId={selecionado?.id}
			/>

			<MainHeader>
				<Title>Planos</Title>
				<MainHeaderButtonsWrapper>
					<Button
						onClick={() => {
							setSelecionado(null);
							setModalOpen(true);
						}}
					>
						<Plus className="h-4 w-4" />
						Criar um plano
					</Button>
				</MainHeaderButtonsWrapper>
			</MainHeader>

			<div className="flex-1 overflow-auto px-6 pb-6">
				<p className="mb-4 max-w-3xl text-sm leading-relaxed text-muted-foreground">
					O pacote que você vende: preço por mês e até onde a empresa pode ir.
				</p>

				<div className="overflow-x-auto rounded-xl border bg-card">
					<Table>
						<TableHeader className="sticky top-0 z-10 bg-card">
							<TableRow>
								<TableHead>Nome do plano</TableHead>
								<TableHead className="text-right">Por mês</TableHead>
								<TableHead className="text-center">
									Usuários
									<span className="block text-[10px] font-normal text-muted-foreground">
										até quantas pessoas
									</span>
								</TableHead>
								<TableHead className="text-center">
									WhatsApps
									<span className="block text-[10px] font-normal text-muted-foreground">
										números conectados
									</span>
								</TableHead>
								<TableHead className="text-center">
									Filas
									<span className="block text-[10px] font-normal text-muted-foreground">
										setores de atendimento
									</span>
								</TableHead>
								<TableHead className="text-center">Pode ser vendido?</TableHead>
								<TableHead className="text-center">Ações</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{plans.map((plan) => (
								<TableRow key={plan.id}>
									<TableCell className="font-medium">{plan.name}</TableCell>
									<TableCell className="text-right tabular-nums">
										{moeda(plan.value)}
									</TableCell>
									<TableCell className="text-center">
										{limite(plan.users)}
									</TableCell>
									<TableCell className="text-center">
										{limite(plan.connections)}
									</TableCell>
									<TableCell className="text-center">
										{limite(plan.queues)}
									</TableCell>
									<TableCell className="text-center">
										{plan.isActive ? (
											<Badge variant="secondary" className="gap-1">
												<Check className="h-3 w-3" />
												Sim
											</Badge>
										) : (
											<Badge
												variant="outline"
												className="gap-1"
												title="Empresas que já usam continuam normalmente"
											>
												<Minus className="h-3 w-3" />
												Não, está fora do catálogo
											</Badge>
										)}
									</TableCell>
									<TableCell>
										<div className="flex items-center justify-center gap-1">
											<Button
												variant="ghost"
												size="icon"
												className="h-8 w-8"
												aria-label="Alterar este plano"
											title="Alterar preço, limites e recursos"
												onClick={() => {
													setSelecionado(plan);
													setModalOpen(true);
												}}
											>
												<Pencil className="h-4 w-4" />
											</Button>
											<Button
												variant="ghost"
												size="icon"
												className="h-8 w-8"
												aria-label="Apagar este plano"
											title="Apagar este plano"
												onClick={() => {
													setSelecionado(plan);
													setConfirmOpen(true);
												}}
											>
												<Trash2 className="h-4 w-4" />
											</Button>
										</div>
									</TableCell>
								</TableRow>
							))}
							{loading && <TableRowSkeleton columns={7} />}
							{!loading && plans.length === 0 && (
								<TableRow>
									<TableCell
										colSpan={7}
										className="py-10 text-center text-sm text-muted-foreground"
									>
										Nenhum plano criado ainda. Sem pelo menos um plano você não
										consegue cadastrar uma empresa cliente — comece pelo botão
										“Criar um plano”.
									</TableCell>
								</TableRow>
							)}
						</TableBody>
					</Table>
				</div>
			</div>
		</MainContainer>
	);
};

export default Plans;
